import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, firstValueFrom, map, Subject, BehaviorSubject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { CheckServerSteps, DropVacancy, GetHardSkills, GetJobTitles, GetJobTitlesSuccess, GetSkillsList, GetSoftSkills, GetVacancy, InitSteps, SaveVacancy, SetCountry, SetCurrency, SetCurrentStep, SetHardSkills, SetJobPosition, SetSeniority, SetSoftSkills, SetVacancyId, StepValid,SetRequireValidation, PublishCompleted, SaveDraftCompleted, SetVacancyStatusFailure, SetJobPositionFailure } from './create-position.actions';
import { CreatePositionState, selectAdditionalInfo, selectCurrentSteps, selectDescriptionPrompt, selectHardSkills, selectJobPosition, selectJobPositionLevel, selectJobPositions, selectJobTitles, selectSalaryRangeParams, selectSoftSkills, selectStepsState, selectVacancyId, selectVacancyStatus,selectRequireValidation, selectAssessments, selectForceHighAccuracy  } from './create-position.store';
import { Actions, ofType } from '@ngrx/effects';
import { ApiService } from '../../services/api.service';

export enum CREATE_POSITION_STEPS {
  jobPosition = 'jobPosition',
  hard = 'hardSkills',
  soft = 'softSkills',
  additional = 'additionalInfo',
  uploadCvs = 'uploadCvs'
}

@Injectable({ providedIn: 'root' })
export class CreatePositionProxy {

  stepsState$ = this.store.select(selectStepsState);
  vacancyStatus$ = this.store.select(selectVacancyStatus);
  currentStep$ = this.store.select(selectCurrentSteps);
  vacancyId$ = this.store.select(selectVacancyId);
  descriptionPrompt$ = this.store.select(selectDescriptionPrompt);
  jobTitles$ = this.store.select(selectJobTitles);
  requireValidation$ = this.store.select(selectRequireValidation);
  jobPosition$ = this.store.select(selectJobPosition);
  jobPositions$ = this.store.select(selectJobPositions);
  jobPositionLevel$ = this.store.select(selectJobPositionLevel);
  hardSkills$ = this.store.select(selectHardSkills);
  salaryRangeParams$ = this.store.select(selectSalaryRangeParams).pipe(
    distinctUntilChanged((pre: any, curr: any) => JSON.stringify(pre) === JSON.stringify(curr))
  );
  selectedHardSkills$ = this.hardSkills$.pipe(
    map(skills => skills ? skills.reduce((filtered, item) => {
      if (item.skills && item.skills.length && item.skills.some(val => val.experience || val.years)) {
        filtered.push({ ...item, skills: item.skills.filter((item: any) => item.experience)});
      }
      return filtered;
    }, []) : [])
  );
  softSkills$ = this.store.select(selectSoftSkills).pipe(
    map(skills => skills ? skills : [])
  );
  defaultSoftSkills$ = this.softSkills$.pipe(
    map(skills => skills.every(item => item.experience === 1))
  )
  additionalInfo$ = this.store.select(selectAdditionalInfo);
  assessments$ = this.store.select(selectAssessments);
  forceHighAccuracy$ = this.store.select(selectForceHighAccuracy);
  submitForm$ = new Subject();
  setJobPositionFailure$ = this.actions$.pipe(
    ofType(SetJobPositionFailure),
    map(action => action.error)
  );
  skillsWasChanged = false;
  forceHighAccuracy = new FormControl(false);
  stepInProgress$ = new BehaviorSubject<boolean>(false);
  isDisableStep$ = new BehaviorSubject<boolean>(false);

  constructor(
    private store: Store<CreatePositionState>,
    private actions$: Actions,
    private apiService: ApiService
  ) {
  }

  setVacancyId(vacancyId) {
    this.store.dispatch(SetVacancyId({ vacancyId }));
  }

  checkServerSteps() {
    this.store.dispatch(CheckServerSteps());
  }

  initSteps() {
    this.store.dispatch(InitSteps());
  }

  setCurrentStep(currentStep: number) {
    this.store.dispatch(SetCurrentStep({ currentStep }));
  }

  async getJobTitles() {
    setTimeout(() => this.store.dispatch(GetJobTitles()));
    return await firstValueFrom(this.actions$.pipe(ofType(GetJobTitlesSuccess)));
  }

  checkValidation(key: string) {
    this.submitForm$.next(key);
  }

  validationSuccess(key: string, data: any = null, wasChanged = false) {
    this.store.dispatch(StepValid({ key, data, wasChanged }));
  }

  setJobPosition(
    jobPosition: any = null,
    jobPositions: any = [],
    jobDescription = '',
    selectedJobDescriptionFile: File | null = null,
    wasChanged = false
  ) {
    this.store.dispatch(SetJobPosition({
      jobPosition,
      jobPositions,
      jobDescription,
      selectedJobDescriptionFile,
      wasChanged
    }))
  }

  setCountry(country: string | null) {
    this.store.dispatch(SetCountry({ country }));
  }

  setCurrency(currency) {
    this.store.dispatch(SetCurrency({ currency }));
  }

  getHardSkills() {
    this.store.dispatch(GetHardSkills());
  }

  setHardSkills(skillsList: any[]) {
    this.store.dispatch(SetHardSkills({ skillsList }));
  }

  getSoftSkills() {
    this.store.dispatch(GetSoftSkills());
  }

  setSoftSkills(skillsList: any[]) {
    this.store.dispatch(SetSoftSkills({ skillsList }));
  }

  getSkillsList() {
    this.store.dispatch(GetSkillsList());
  }

  saveVacancy(publish = false, redirect = true) {
    this.store.dispatch((SaveVacancy({ publish, redirect })));
  }

  dropVacancy() {
    this.store.dispatch((DropVacancy()));
  }

  async setSeniority(level) {
    setTimeout(() => this.store.dispatch((SetSeniority({ level }))));
    return await firstValueFrom(this.actions$.pipe(ofType(GetVacancy)));
  }

  getSalaryRange(params) {
    return this.apiService.getSalaryRange(params);
  }

  getIndianCities() {
    return this.apiService.getCities();
  }

  enhanceDescriptionPrompt(descriptionPrompt: string) {
    return this.apiService.enhanceDescriptionPrompt(descriptionPrompt);
  }

  setDisableStep(value: boolean) {
    this.isDisableStep$.next(value);
  }

  setRequireValidation(value: boolean) {
  this.store.dispatch(SetRequireValidation({ value }));
}


  async publishCompleted() {
    return await firstValueFrom(
    this.actions$.pipe(ofType(PublishCompleted, SetVacancyStatusFailure),
  map(action => {
          if (action.type === PublishCompleted.type) {
            return { ok: true, data: { vacancyId : action.vacancyId , status: action.status, approvedEmailAutomation :action.approvedEmailAutomation} };
          } else {
            return { ok: false, error: (action as any).error };
          }
        }))
  );
  }

  async saveDraftCompleted() {
    return await firstValueFrom(
      this.actions$.pipe(ofType(SaveDraftCompleted, SetVacancyStatusFailure),
        map(action => {
          if (action.type === SaveDraftCompleted.type) {
            return { ok: true };
          } else {
            return { ok: false, error: (action as any).error };
          }
        })
      )
    );
  }

}
